import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from "react";
import ReactDOM from "react-dom";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import { MoreHorizontal } from "lucide-react";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import PaginationControls from "../../../../components/ACS-Dashboard/ACS-Blocks/PaginationControls";
import { usePagination } from "../hooks/usePagination";
import { useDataFetching } from "../hooks/useDataFetching";
import { laneApi } from "../api/laneApi";
import { safe } from "../utils/utils";
import { contractApi } from "../api/contractApi";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/contractsSlice";
import {
  useTableSavedViews,
  TableSavedViewsControl,
  TABLE_PAGE_KEYS,
} from "../../../TableSavedViews";
import { buildFilterNode, countActiveFilters } from "../../../AdvancedFilter/filterTree";
import { AdvancedFilterToolbar } from "../../../AdvancedFilter/AdvancedFilterToolbar";
import { AdvancedFilterModal } from "../../../AdvancedFilter/AdvancedFilterModal";
import { SavedFiltersControl } from "../../../AdvancedFilter/SavedFiltersControl";
import { LANE_RULE_FIELDS } from "../lanesFilterFields";


const HEADERS = [
  "Selection",
  "Action",
  "Contract Code",
  "Lane Code",
  "Office Code",
  "Name",
  "Origin Country",
  "Origin State",
  "Origin City",
  "Origin Zip Code",
  "Destination Country",
  "Destination State",
  "Destination City",
  "Destination Zip Code",
  "Via Points (Facility)",
  "Assoc.Routes",
];

const SORTABLE_COLUMNS = {
  "Contract Code":         "contract.contractCode",
  "Lane Code":             "laneCode",
  "Office Code":           "officeCode",
  "Name":                  "name",
  "Origin Country":        "originCountry",
  "Origin State":          "originState",
  "Origin City":           "originCity",
  "Origin Zip Code":       "origin",
  "Destination Country":   "destinationCountry",
  "Destination State":     "destinationState",
  "Destination City":      "destinationCity",
  "Destination Zip Code":  "destination",
  "Via Points (Facility)": "viaPointsFacility",
  "Assoc.Routes":          "assocRoutes",  // in-memory sort
};

const formatFacilityType = (type) => {
  if (!type) return "-";
  return type
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const formatViaPoints = (lane) => {
  const points = [];
  const addPoint = (point, type) => {
    if (point) return `${point}${type ? ` (${formatFacilityType(type)})` : ""}`;
    return null;
  };
  [lane.viaPoint, lane.viaPoint2, lane.viaPoint3].forEach((point, index) => {
    const formatted = addPoint(point, lane[`viaPoint${index > 0 ? index + 1 : ""}Type`]);
    if (formatted) points.push(formatted);
  });
  return points.length > 0
    ? points.join(" → ")
    : <span className="text-gray-400 dark:text-gray-500">No via points</span>;
};

const LanesDetails = ({ officeCode, searchTerm = null, fetchFn = null }) => {
  const { pagination, setPagination, handlePageChange, handlePerPageChange } = usePagination();

  const [sortBy,     setSortBy]     = useState("updatedAt");
  const [ascending,  setAscending]  = useState(false);
  const [lanes,         setLanes]         = useState([]);
  const [isLoading,     setIsLoading]     = useState(true);
  const [selectedLanes, setSelectedLanes] = useState([]);
  const [openMenu,      setOpenMenu]      = useState(null);
  const [dropdownPos,   setDropdownPos]   = useState({ top: 0, left: 0 });
  const [columnWidths, setColumnWidths] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState({ rules: [], matchType: "AND" });
  const [showFilterModal, setShowFilterModal] = useState(false);


  const dropdownRef       = useRef(null);
  const checkboxHeaderRef = useRef(null);
  const dispatch          = useDispatch();
  const tableViews = useTableSavedViews({
    pageKey: TABLE_PAGE_KEYS.CONTRACT_LANES,
    allHeaders: HEADERS,
    fixedPrefixCount: 2,
    defaultStickyColumnKeys: ["Selection", "Action", "Contract Code"],
  });
  const {
    dataHeaders,
    controlLabel,
    views,
    activeView,
    loading: viewsLoading,
    error: viewsError,
    isDirty,
    selectView,
    saveNewView,
    updateActiveView,
    renameView,
    deleteView,
    setDefaultView,
    updateColumnVisibility,
    columnManagerProps,
    onManageColumns,
  } = tableViews;

  // Close portal dropdown on outside click
  useEffect(() => {
    const handle = (e) => {
      if (e.target.closest("[data-action-trigger]")) return;
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setOpenMenu(null);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const handleActionClick = (id, e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
    setOpenMenu((prev) => (prev === id ? null : id));
  };

  const formattedLanes = useMemo(
    () =>
      lanes.map((lane) => ({
        ...lane,
        "Contract Code":        safe(() => lane.contractCode),
        "Lane Code":            safe(() => lane.laneCode),
        "Office Code":          safe(() => lane.officeCode),
        Name:                   safe(() => lane.name),
        "Origin Country":       safe(() => lane.originCountry),
        "Origin State":         safe(() => lane.originState),
        "Origin City":          safe(() => lane.originCity),
        "Origin Zip Code":      safe(() => lane.origin),
        "Destination Country":  safe(() => lane.destinationCountry),
        "Destination State":    safe(() => lane.destinationState),
        "Destination City":     safe(() => lane.destinationCity),
        "Destination Zip Code": safe(() => lane.destination),
        "Via Points (Facility)": safe(
          () => formatViaPoints(lane),
          "string",
          <span className="text-gray-400 dark:text-gray-500">No via points</span>
        ),
        "Assoc.Routes": safe(() => lane.routeIds?.length, "number", 0),
      })),
    [lanes]
  );

  const handleRowDoubleClick = useCallback(async (rowId) => {
    const selectedLaneData = formattedLanes.find((lane) => lane.id === rowId);
    if (!selectedLaneData) return;

    try {
      const contractIdToUse = selectedLaneData.contractId || selectedLaneData.contract?.id;
      const laneOfficeCode  = selectedLaneData.officeCode || officeCode;

      if (!contractIdToUse) {
        console.error("No contract ID found for lane:", selectedLaneData);
        return;
      }

      let contractData;
      try {
        const res  = await contractApi.getContractById(laneOfficeCode, contractIdToUse);
        contractData = res.data || res;
      } catch {
        contractData = {
          contractId:   selectedLaneData.contractId,
          contractCode: selectedLaneData["Contract Code"],
          officeCode:   selectedLaneData.officeCode,
          status:       "ACTIVE",
          effectiveDate:   selectedLaneData.effectiveDate   || null,
          expirationDate:  selectedLaneData.expirationDate  || null,
          laneIds: [rowId],
        };
      }

      dispatch(addTab({
        tabData: {
          name:            `Contract: ${contractData.contractCode || selectedLaneData["Contract Code"]}`,
          type:            "contractDetails",
          officeCode,
          contractId:      selectedLaneData.contractId || contractIdToUse,
          contractData,
          expandedDataKey: `lane-${rowId}`,
        },
      }));
    } catch (error) {
      console.error("Error opening contract details:", error);
    }
  }, [formattedLanes, officeCode, dispatch]);

  const handleSort = (newSortBy) => {
    if (sortBy !== newSortBy) {
      setSortBy(newSortBy);
      setAscending(true);
    } else {
      setAscending((prev) => !prev);
    }
  };

  const onHeaderSortClick = (headerLabel) => {
    const key = SORTABLE_COLUMNS[headerLabel];
    if (!key) return;
    handleSort(key);
  };

  // Memoized: buildFilterNode returns a new object every call; an unmemoized recompute
  // here changes fetchLanes' identity every render, retriggering its effect in a loop.
  const filterNode = useMemo(
    () => buildFilterNode(LANE_RULE_FIELDS, advancedFilters),
    [advancedFilters]
  );

  const fetchLanes = useCallback(
    (params) => {
      const apiParams = {
        page: pagination.currentPage - 1,
        size: pagination.perPage,
        sortBy,
        ascending,
        ...params,
      };
      if (searchTerm && fetchFn) {
        return fetchFn({ officeCode, query: searchTerm, status: "", filter: filterNode }, apiParams);
      }
      return laneApi.searchLanes({ filter: filterNode }, apiParams);
    },
    [searchTerm, fetchFn, officeCode, pagination.currentPage, pagination.perPage, sortBy, ascending, filterNode]
  );

  useDataFetching({
    fetchFn: fetchLanes,
    officeCode,
    pagination,
    onSuccess: useCallback((data) => {
      setLanes(data.content);
      setPagination((prev) => ({ ...prev, total: data.totalElements }));
      setIsLoading(false);
    }, [setPagination]),
    onError: () => setIsLoading(false),
  });

  const handleSelectAll      = (checked) => setSelectedLanes(checked ? lanes.map((l) => l.id) : []);
  const handleSingleCheckbox = (id) =>
    setSelectedLanes((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  return (
    <div className="bg-white dark:bg-acs-dark-surface p-1 rounded-xl shadow-md flex flex-col h-full transition-none">
      {isLoading ? (
        <Loader color="text-blue-600 dark:text-acs-dark-text" size="h-5 w-5" message="Loading Lanes..." />
      ) : (
        <>
          {!searchTerm && (
            <AdvancedFilterToolbar
              ruleFields={LANE_RULE_FIELDS}
              filters={advancedFilters}
              activeCount={countActiveFilters(LANE_RULE_FIELDS, advancedFilters)}
              onOpenModal={() => setShowFilterModal(true)}
              onClearAll={() => setAdvancedFilters({ rules: [], matchType: "AND" })}
              onChangeFilters={setAdvancedFilters}
              trailing={
                <div className="flex items-center gap-2">
                  <SavedFiltersControl
                    pageKey={TABLE_PAGE_KEYS.CONTRACT_LANES}
                    ruleFields={LANE_RULE_FIELDS}
                    currentFilters={advancedFilters}
                    onApply={setAdvancedFilters}
                  />
              <TableSavedViewsControl
                controlLabel={controlLabel}
                views={views}
                activeView={activeView}
                loading={viewsLoading}
                error={viewsError}
                isDirty={isDirty}
                allDataHeaders={HEADERS.slice(2)}
                dataHeaders={dataHeaders}
                onSelectView={selectView}
                onSaveNew={saveNewView}
                onUpdateActive={updateActiveView}
                onRename={renameView}
                onDelete={deleteView}
                onSetDefault={setDefaultView}
                onUpdateColumnVisibility={updateColumnVisibility}
                onManageColumns={onManageColumns}
              />
                </div>
              }
            />
          )}

          {searchTerm && (
            <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
              <TableSavedViewsControl
                controlLabel={controlLabel}
                views={views}
                activeView={activeView}
                loading={viewsLoading}
                error={viewsError}
                isDirty={isDirty}
                allDataHeaders={HEADERS.slice(2)}
                dataHeaders={dataHeaders}
                onSelectView={selectView}
                onSaveNew={saveNewView}
                onUpdateActive={updateActiveView}
                onRename={renameView}
                onDelete={deleteView}
                onSetDefault={setDefaultView}
                onUpdateColumnVisibility={updateColumnVisibility}
                onManageColumns={onManageColumns}
              />
            </div>
          )}
{showFilterModal && (
            <AdvancedFilterModal
              ruleFields={LANE_RULE_FIELDS}
              filters={advancedFilters}
              onApply={setAdvancedFilters}
              onClose={() => setShowFilterModal(false)}
            />
          )}
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none">
            <Table
              {...columnManagerProps}
              data={formattedLanes}
              initialColumnWidths={columnWidths}
              onColumnResize={setColumnWidths}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              selectedRows={selectedLanes}
              totalValidRows={formattedLanes.length}
              onSelectAll={handleSelectAll}
              renderCheckboxActions={() => {}}
              checkboxHeaderRef={checkboxHeaderRef}
              sortBy={sortBy}
              ascending={ascending}
              onSort={onHeaderSortClick}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
            >
              {(row, _rowIdx, _columnWidths, getStickyCellProps, visibleColumns) => {
                const id     = row.id;
                const isOpen = openMenu === id;
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((id) => id !== "Selection" && id !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                return (
                  <>
                    {/* Column 0 — Checkbox */}
                    <td
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${getStickyCellProps("Selection").className}`}
                      style={{ ...getStickyCellProps("Selection").style, width: 80, minWidth: 80, maxWidth: 80 }}
                    >
                      <input
                        type="checkbox"
                        className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox dark:checked:border-acs-checkbox-dark after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                        checked={selectedLanes.includes(id)}
                        onChange={() => handleSingleCheckbox(id)}
                      />
                    </td>

                    {/* Column 1 — Action (portal dropdown) */}
                    <td
                      onClick={(e) => e.stopPropagation()}
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${getStickyCellProps("Action").className}`}
                      style={{ ...getStickyCellProps("Action").style, width: 50, minWidth: 50, maxWidth: 50 }}
                    >
                      <button
                        data-action-trigger
                        className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text w-full"
                        onClick={(e) => handleActionClick(id, e)}
                      >
                        <MoreHorizontal size={18} className="mx-auto" />
                      </button>
                    </td>

                    {isOpen && ReactDOM.createPortal(
                      <div
                        ref={dropdownRef}
                        style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                      >
                        <ActionMenu
                          rowId={id}
                          isOpen
                          moduleName="Lane"
                          onClose={() => setOpenMenu(null)}
                          actions={[
                            { icon: "Eye", label: "View", color: "blue", action: () => handleRowDoubleClick(id) },
                          ]}
                        />
                      </div>,
                      document.body
                    )}

                    {/* Columns 2+ — Data */}
                    {dataHeadersToRender.map((col) => {
                      return (
                        <td
                          key={col}
                          className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${getStickyCellProps(col).className}`}
                          style={{
                            ...getStickyCellProps(col).style,
                            whiteSpace:   "nowrap",
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            width:        "200px",
                            minWidth:     "200px",
                            maxWidth:     "200px",
                          }}
                          onDoubleClick={() => handleRowDoubleClick(id)}
                        >
                          {row[col]}
                        </td>
                      );
                    })}
                  </>
                );
              }}
            </Table>
          </div>


          <PaginationControls
            pagination={pagination}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
            moduleName="Lanes"
            className="mt-4 p-2 border-t border-gray-200 dark:border-acs-dark-border"
          />
        </>
      )}
    </div>
  );
};

export default LanesDetails;
